import React, { memo, useCallback, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import BadgeCDS from 'app/shared/componentsRestyled/coreDesignSystem/BadgeCDS';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import { BOTTOM_NAV_ICON_SIZE } from '../../BottomNav.constants';

import {
  cardHeadStyles,
  cardRowLabelActiveColor,
  cardRowLabelStyles,
  cardRowRightStyles,
  cardRowTextStyles,
  getCardChipSx,
  getCardRowSx,
  getCardSx,
  miniBadgeStyles,
  rowArrowWrapperStyles,
  scrimStyles,
} from './NavGroupCard.styles';

/** Minimum unread count before the badge is rendered. */
const BADGE_MIN = 1;

/** Maximum unread count shown in the member mini-badge before capping. */
const BADGE_MAX = 99;

const memberShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  actionId: PropTypes.string,
  badgeCount: PropTypes.number,
  icon: PropTypes.elementType,
  url: PropTypes.string,
});

/**
 * A single member row inside the cohesive speed-dial card.
 *
 * @param {Object} props
 * @param {number} props.animationIndex - stagger order (0-based) for the row fade-in
 * @param {boolean} props.isActive - whether this member is the currently active route
 * @param {Object} props.member - NavGroupMember descriptor
 * @param {Function} props.onSelect - called with the member when the row is tapped
 */
const NavGroupCardRow = memo(({ animationIndex, isActive, member, onSelect }) => {
  const IconComponent = member.icon;

  const onPress = useCallback(() => {
    onSelect(member);
  }, [member, onSelect]);

  const cappedBadge = Math.min(member.badgeCount ?? 0, BADGE_MAX);
  const badgeLabel = cappedBadge === BADGE_MAX ? `${BADGE_MAX}+` : String(cappedBadge);
  const hasBadge = (member.badgeCount ?? 0) >= BADGE_MIN;

  const rowSx = useMemo(() => getCardRowSx(isActive, animationIndex), [isActive, animationIndex]);

  const chipSx = useMemo(() => getCardChipSx(isActive), [isActive]);

  return (
    <Box
      aria-current={isActive ? 'page' : undefined}
      component="button"
      sx={rowSx}
      onClick={onPress}
    >
      <Box sx={chipSx}>{IconComponent && <IconComponent size={BOTTOM_NAV_ICON_SIZE} />}</Box>

      <Box sx={cardRowTextStyles}>
        <Typography
          color={isActive ? cardRowLabelActiveColor : 'text.primary'}
          sx={cardRowLabelStyles}
          variant="medium14"
        >
          {member.label}
        </Typography>
      </Box>

      <Box sx={cardRowRightStyles}>
        {hasBadge && (
          <Box sx={miniBadgeStyles}>
            <BadgeCDS value={badgeLabel} />
          </Box>
        )}
        <Box sx={rowArrowWrapperStyles}>
          <ArrowCheckIcon direction={IconDirection.RIGHT} size={IconSize[20]} />
        </Box>
      </Box>
    </Box>
  );
});

NavGroupCardRow.propTypes = {
  animationIndex: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  member: memberShape.isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * Frosted glass speed-dial card that appears above a long-pressed tab.
 *
 * Renders a header (group label), then one row per member with staggered fade-in.
 * A tail points downward toward the triggering tab.
 *
 * @param {Object} props
 * @param {string|null} props.activeUrl - current route path (to highlight the active row)
 * @param {number} props.anchorLeft - card left edge (px, relative to viewport)
 * @param {number} props.bottomOffset - card bottom edge (px from viewport bottom)
 * @param {string} [props.groupLabel] - uppercase group header text
 * @param {Array<Object>} props.members - NavGroupMember descriptors
 * @param {Function} props.onScrimClick - called when the backdrop scrim is tapped
 * @param {Function} props.onSelect - called with a NavGroupMember when a row is tapped
 * @param {number} props.tailOffset - horizontal offset of the tail within the card (px)
 */
const NavGroupCard = ({
  activeUrl,
  anchorLeft,
  bottomOffset,
  groupLabel,
  members,
  onScrimClick,
  onSelect,
  tailOffset,
}) => {
  const cardSx = useMemo(
    () => getCardSx(anchorLeft, bottomOffset, tailOffset),
    [anchorLeft, bottomOffset, tailOffset],
  );

  return (
    <>
      <Box sx={scrimStyles} onClick={onScrimClick} />

      <Box sx={cardSx}>
        {groupLabel && (
          <Typography component="div" sx={cardHeadStyles} variant="medium12">
            {groupLabel}
          </Typography>
        )}

        {members.map((member, index) => {
          const isActive = !!(member.url && activeUrl && activeUrl.startsWith(member.url));

          return (
            <NavGroupCardRow
              animationIndex={index}
              isActive={isActive}
              key={member.id}
              member={member}
              onSelect={onSelect}
            />
          );
        })}
      </Box>
    </>
  );
};

NavGroupCard.propTypes = {
  anchorLeft: PropTypes.number.isRequired,
  bottomOffset: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(memberShape).isRequired,
  tailOffset: PropTypes.number.isRequired,
  onScrimClick: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  activeUrl: PropTypes.string,
  groupLabel: PropTypes.string,
};

export default memo(NavGroupCard);
